import { HttpErrorResponse } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { Action } from "@ngrx/store";
import { Observable, of } from "rxjs";
import { catchError, exhaustMap, map, mergeMap } from "rxjs/operators";
import { AuthenticationService, BasicFirmwareInfo, DeviceProfilesService, DevicesService, FirmwareService, MACService, MacRange, PermissionsApi, ProducedDevice } from "src/app/generated/CrossPoint.ProductionTooling.Server";
import { errorEventAction } from "../actions/app.actions";
import { ProductionToolingActionsTypes, addMacRangeFailedAction, addMacRangeSuccessAction, deleteMacRangeFailedAction, deleteMacRangeSuccessAction, getAuthInfoProductionToolingFailedAction, getAuthInfoProductionToolingSuccessAction, getDeviceProfilesAction, getDeviceProfilesFailedAction, getDeviceProfilesSuccessAction, getDevicesFailedAction, getDevicesSuccessAction, getFirmwareFailedAction, getFirmwareSuccessAction, getMacRangesFailedAction, getMacRangesSuccessAction, setDeviceProfilesFailedAction, setDeviceProfilesSuccessAction } from "../actions/production.actions";
import { IGetDevicesData } from "src/app/utils/interfaces/production.interfaces";
import { IDataAction } from "src/app/utils/interfaces/common.interfaces";
import { DeviceProfiles, ETagItem } from "src/app/utils/interfaces/store.interfaces";


@Injectable()
export class ProductionToolingEffects {

  public getAuthInfoXpacProxyEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.GET_AUTH_INFO_PRODUCTION_TOOLING),
        exhaustMap(() => this.authenticationService.getPermissions()
          .pipe(
            map((permissions: PermissionsApi) => getAuthInfoProductionToolingSuccessAction({ data: permissions.permissions })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetAuthInfoProductionToolingErrorHasOccurred" } }), getAuthInfoProductionToolingFailedAction())
            })
          )
        )
      )
  );

  public getDevicesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.GET_DEVICES),
        exhaustMap(({ data: { searchText, sinceUtc, beforeUtc, limit } }: IDataAction<IGetDevicesData>) => this.deviceService.getDevices(searchText, sinceUtc, beforeUtc, limit)
          .pipe(
            map((devices: ProducedDevice[]) => getDevicesSuccessAction({ data: devices })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDevicesFailed" } }), getDevicesFailedAction())
            })
          )
        )
      )
  );

  public getFirmwareEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.GET_FIRWMARE),
        exhaustMap(() => this.firmwareService.getFirmware()
          .pipe(
            map((firmware: BasicFirmwareInfo[]) => getFirmwareSuccessAction({ data: firmware })),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetFirmwareFailed" } }), getFirmwareFailedAction())
            })
          )
        )
      )
  );

  public getDeviceProfilesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.GET_PROFILES),
        // We want to bypass the cache for this page so we always get the real stored config to edit
        exhaustMap(() => this.deviceProfileService.getDeviceProfiles(true)
          .pipe(
            map((deviceProfiles) => {
              const etag: string = deviceProfiles.etag;
              return getDeviceProfilesSuccessAction({ data: { etag, data: deviceProfiles } });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetDeviceProfilesFailed" } }), getDeviceProfilesFailedAction())
            })
          )
        )
      )
  );

  public setDeviceProfilesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.SET_PROFILES),
        exhaustMap(({ data: { etag, data } }: IDataAction<ETagItem<DeviceProfiles>>) =>
          this.deviceProfileService.setDeviceProfiles(etag, data)
            .pipe(
              mergeMap(() => [
                setDeviceProfilesSuccessAction({ data: { etag, data } }),
                // We need the new ETag if we want to make more changes, so refresh the data
                getDeviceProfilesAction(),
              ]),
              catchError((error: HttpErrorResponse) => {
                return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.SetDeviceProfilesFailed" } }), setDeviceProfilesFailedAction())
              })
            )
        )
      )
  );

  public getMacRangesEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.GET_MAC_RANGES),
        exhaustMap(() => this.macService.getMacRanges()
          .pipe(
            map((ranges) => {
              return getMacRangesSuccessAction({ data: ranges });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.GetMacRangesFailed" } }), getMacRangesFailedAction())
            })
          )
        )
    )
  );


  public addMacRangeEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.ADD_MAC_RANGE),
        mergeMap(({ data: range }: IDataAction<MacRange>) => this.macService.addMacRange(range.name, range.firstMac, range.lastMac)
          .pipe(
            map((addedRange) => {
              return addMacRangeSuccessAction({ data: addedRange });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.AddMacRangeFailed" } }), addMacRangeFailedAction({ data: range }))
            })
          )
        )
      )
  );

  public deleteMacRangeEffect$: Observable<Action> = createEffect(() =>
    this.actions$
      .pipe(
        ofType(ProductionToolingActionsTypes.DELETE_MAC_RANGE),
        mergeMap(({ data: rangeId }: IDataAction<string>) => this.macService.deleteMacRange(rangeId)
          .pipe(
            map(() => {
              return deleteMacRangeSuccessAction({ data: rangeId });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(errorEventAction({ data: { error: error, translationkey: "StoreActionFailed.DeleteMacRangeFailed" } }), deleteMacRangeFailedAction({ data: rangeId }))
            })
          )
        )
      )
  );

  constructor(
    private actions$: Actions,   
    private authenticationService: AuthenticationService,
    private deviceService: DevicesService,
    private deviceProfileService: DeviceProfilesService,
    private firmwareService: FirmwareService,
    private macService: MACService,
  ) { }
}
